'use client'

import { useEffect, useState } from 'react'
import { Clock, X } from 'lucide-react'

const STORAGE_KEY = 'promptvan:recent-searches'
const MAX_RECENT = 5

export function getRecentSearches(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export function addRecentSearch(query: string) {
  if (typeof window === 'undefined') return
  const trimmed = query.trim()
  if (trimmed.length < 2) return
  try {
    const existing = getRecentSearches().filter((s) => s !== trimmed)
    const updated = [trimmed, ...existing].slice(0, MAX_RECENT)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // localStorage unavailable
  }
}

export function removeRecentSearch(query: string) {
  if (typeof window === 'undefined') return
  try {
    const updated = getRecentSearches().filter((s) => s !== query)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // localStorage unavailable
  }
}

interface SearchRecentProps {
  onSelect: (query: string) => void
}

export function SearchRecent({ onSelect }: SearchRecentProps) {
  const [searches, setSearches] = useState<string[]>([])

  useEffect(() => {
    setSearches(getRecentSearches())
  }, [])

  const handleRemove = (query: string) => {
    removeRecentSearch(query)
    setSearches((prev) => prev.filter((s) => s !== query))
  }

  if (searches.length === 0) return null

  return (
    <div className="px-2 py-3">
      <p className="px-2 pb-2 text-xs font-medium text-[var(--color-text-muted)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
        Recent searches
      </p>
      {searches.map((query) => (
        <div
          key={query}
          className="flex items-center rounded-lg px-2 py-1.5 hover:bg-[var(--color-bg-elevated)]"
        >
          <button
            type="button"
            onClick={() => onSelect(query)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-[var(--color-text-secondary)]"
            style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
            title={query}
          >
            <Clock className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" />
            <span className="truncate">{query}</span>
          </button>
          <button
            type="button"
            onClick={() => handleRemove(query)}
            className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            aria-label={`Remove "${query}" from recent searches`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  )
}
